import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import useAPI from '@/hooks/useAPI';
import AppointmentsRepository, {
  GetAppointmentsParams,
  GetScheduleItemsParams
} from '@/repositories/AppointmentsRepository';
import {
  Appointment,
  SyncUserAppointment,
  SyncUserAppointmentResponse
} from '@/types/appointments';

export const useAppointmentAttendanceInsights = (personId: string) => {
  const appointmentsRepository = AppointmentsRepository();

  return useAPI({
    queryKey: ['getAppointmentAttendanceInsights', personId],
    queryFn: async () => {
      return await appointmentsRepository.getAppointmentAttendanceInsights(personId);
    }
  });
};

export const useAppointment = (appointmentId: string) => {
  const appointmentsRepository = AppointmentsRepository();

  return useAPI({
    queryKey: ['getAppointment', appointmentId],
    queryFn: async () => {
      return await appointmentsRepository.getAppointment(appointmentId);
    }
  });
};

export const useAppointmentAttendances = (
  params: GetAppointmentsParams,
  options?: { staleTime?: number }
) => {
  const appointmentsRepository = AppointmentsRepository();

  return useAPI({
    queryKey: ['getAppointments', params],
    queryFn: async () => {
      return await appointmentsRepository.getAppointments(params);
    },
    staleTime: options?.staleTime
  });
};

export const useScheduleItems = (
  params: GetScheduleItemsParams,
  options?: { staleTime?: number; enabled?: boolean }
) => {
  const appointmentsRepository = AppointmentsRepository();

  return useAPI({
    queryKey: ['getScheduleItems', params],
    queryFn: async () => {
      return await appointmentsRepository.getScheduleItems(params);
    },
    staleTime: options?.staleTime,
    enabled: options?.enabled
  });
};

export const useAppointmentSync = (
  options?: UseMutationOptions<SyncUserAppointmentResponse, AxiosError, SyncUserAppointment>
) => {
  const appointmentsRepository = AppointmentsRepository();

  return useMutation<SyncUserAppointmentResponse, AxiosError, SyncUserAppointment>({
    mutationFn: (body: SyncUserAppointment) => appointmentsRepository.syncAppointment(body),
    ...options
  });
};

export const useCreateAppointment = (
  options?: UseMutationOptions<Appointment, AxiosError, Record<string, unknown>>
) => {
  const appointmentsRepository = AppointmentsRepository();

  return useMutation<Appointment, AxiosError, Record<string, unknown>>({
    mutationFn: (body: Record<string, unknown>) => appointmentsRepository.createAppointment(body),
    ...options
  });
};
